import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import Layout from './components/layout/Layout';
import { generateWebsiteSchema } from './utils/schemaHelpers';
import DataNotice from './components/shared/DataNotice';

const ErrorFallback = () => (
  <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 p-8"
       style={{ color: 'var(--text-secondary)' }}>
    <span className="material-symbols-outlined text-[#a4262c] text-[48px]">error</span>
    <p className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)' }}>
      Something went wrong
    </p>
    <p className="text-[13px]">Try refreshing the page.</p>
  </div>
);

const HomePage = lazy(() => import('./pages/HomePage'));
const LogicAppsPage = lazy(() => import('./pages/LogicAppsPage'));
const FunctionsPage = lazy(() => import('./pages/FunctionsPage'));
const AppServicePage = lazy(() => import('./pages/AppServicePage'));
const BlobStoragePage = lazy(() => import('./pages/BlobStoragePage'));
const BandwidthPage = lazy(() => import('./pages/BandwidthPage'));
const DisclaimerPage = lazy(() => import('./pages/DisclaimerPage'));
const AIAssistantPage = lazy(() => import('./pages/AIAssistantPage'));
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage'));
const LogAnalyticsPage = lazy(() => import('./pages/LogAnalyticsPage'));
const SC200StudyGuidePage = lazy(() => import('./pages/certifications/SC200StudyGuidePage'));
const LogAnalyticsPricingUKPage = lazy(() => import('./pages/guides/LogAnalyticsPricingUKPage'));
const AZ500StudyGuidePage = lazy(() => import('./pages/certifications/AZ500StudyGuidePage'));
const AZ900StudyGuidePage = lazy(() => import('./pages/certifications/AZ900StudyGuidePage'));
const SearchJobVsRestorePage = lazy(() => import('./pages/guides/SearchJobVsRestorePage'));
const BlobStoragePricingUKPage = lazy(() => import('./pages/guides/BlobStoragePricingUKPage'));
const AZ104StudyGuidePage = lazy(() => import('./pages/certifications/AZ104StudyGuidePage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const BandwidthCostsPage = lazy(() => import('./pages/guides/BandwidthCostsPage'));
const AzureNetworkingCostsGuide = lazy(() => import('./pages/guides/AzureNetworkingCostsGuide'));
const EstimatePage = lazy(() => import('./pages/EstimatePage'));
const SavedEstimatesPage = lazy(() => import('./pages/SavedEstimatesPage'));
const PriceHistoryPage = lazy(() => import('./pages/PriceHistoryPage'));
const GuidedWalkthroughPage = lazy(() => import('./pages/GuidedWalkthroughPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const KqlBuilderPage = lazy(() => import('./pages/KqlBuilderPage'));
const VMCalculatorPage = lazy(() => import('./pages/VMCalculatorPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const MethodologyPage = lazy(() => import('./pages/MethodologyPage'));
const NetworkingPage = lazy(() => import('./pages/NetworkingPage'));
const DataFactoryPage = lazy(() => import('./pages/DataFactoryPage'));
const AzureDataFactoryPricingGuide = lazy(() => import('./pages/guides/AzureDataFactoryPricingGuide'));
const AzureBillingTrapsPage = lazy(() => import('./pages/guides/AzureBillingTrapsPage'));
const InvoiceReconciliationPage = lazy(() => import('./pages/guides/InvoiceReconciliationPage'));
const PromptAnswersPage = lazy(() => import('./pages/PromptAnswersPage'));
const VirtualDesktopPage = lazy(() => import('./pages/VirtualDesktopPage'));
const AzureVirtualDesktopCostGuide = lazy(() => import('./pages/guides/AzureVirtualDesktopCostGuide'));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'));
const FrontDoorPage = lazy(() => import('./pages/FrontDoorPage'));
const AzureFrontDoorPricingGuide = lazy(() => import('./pages/guides/AzureFrontDoorPricingGuide'));
const GuidesHubPage = lazy(() => import('./pages/guides/GuidesHubPage'));
const SQLDatabasePage = lazy(() => import('./pages/SQLDatabasePage'));
const AzureSQLPricingGuide = lazy(() => import('./pages/guides/AzureSQLPricingGuide'));
const FirewallPage = lazy(() => import('./pages/FirewallPage'));
const AzureFirewallPricingGuide = lazy(() => import('./pages/guides/AzureFirewallPricingGuide'));
const OpenAIPage = lazy(() => import('./pages/OpenAIPage'));
const AzureOpenAIPricingGuide = lazy(() => import('./pages/guides/AzureOpenAIPricingGuide'));
const ManagedDisksPage = lazy(() => import('./pages/ManagedDisksPage'));
const AzureManagedDisksPricingGuide = lazy(() => import('./pages/guides/AzureManagedDisksPricingGuide'));
const KeyVaultPage = lazy(() => import('./pages/KeyVaultPage'));
const AzureKeyVaultPricingGuide = lazy(() => import('./pages/guides/AzureKeyVaultPricingGuide'));
const AzureDevOpsPage = lazy(() => import('./pages/AzureDevOpsPage'));
const AzureDevOpsPricingGuide = lazy(() => import('./pages/guides/AzureDevOpsPricingGuide'));
const SiteRecoveryPage = lazy(() => import('./pages/SiteRecoveryPage'));
const AzureSiteRecoveryPricingGuide = lazy(() => import('./pages/guides/AzureSiteRecoveryPricingGuide'));
const AzureBackupPricingUkGuide = lazy(() => import('./pages/guides/AzureBackupPricingUkGuide'));
const AksCalculatorPage = lazy(() => import('./pages/AksCalculatorPage'));
const AzureAksPricingUkGuide = lazy(() => import('./pages/guides/AzureAksPricingUkGuide'));
const SqlMiCalculatorPage = lazy(() => import('./pages/SqlMiCalculatorPage'));
const AzureSqlManagedInstancePricingUkGuide = lazy(() => import('./pages/guides/AzureSqlManagedInstancePricingUkGuide'));
const ApplicationGatewayPage = lazy(() => import('./pages/ApplicationGatewayPage'));
const VpnGatewayPage = lazy(() => import('./pages/VpnGatewayPage'));
const BastionPage = lazy(() => import('./pages/BastionPage'));
const AzureBackupPage = lazy(() => import('./pages/AzureBackupPage'));

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <BrowserRouter>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(generateWebsiteSchema())}
        </script>
      </Helmet>
      <DataNotice />
      <Layout>
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<AIAssistantPage />} />
          <Route path="/calculators" element={<HomePage />} />
          <Route path="/blob-storage" element={<BlobStoragePage />} />
          <Route path="/logic-apps" element={<LogicAppsPage />} />
          <Route path="/functions" element={<FunctionsPage />} />
          <Route path="/app-service" element={<AppServicePage />} />
          <Route path="/bandwidth" element={<BandwidthPage />} />
          <Route path="/firewall" element={<FirewallPage />} />
          <Route path="/log-analytics" element={<LogAnalyticsPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/ai" element={<Navigate to="/" replace />} />
          <Route path="/calculator" element={<Navigate to="/blob-storage" replace />} />
          <Route path="/saved" element={<SavedEstimatesPage />} />
          <Route path="/estimate/:id" element={<EstimatePage />} />
          <Route path="/history" element={<PriceHistoryPage />} />
          <Route path="/guided" element={<GuidedWalkthroughPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/kql-builder" element={<KqlBuilderPage />} />
          <Route path="/virtual-machines" element={<VMCalculatorPage />} />
          <Route path="/networking" element={<NetworkingPage />} />
          <Route path="/data-factory" element={<DataFactoryPage />} />
          <Route path="/virtual-desktop" element={<VirtualDesktopPage />} />
          <Route path="/front-door" element={<FrontDoorPage />} />
          <Route path="/sql-database" element={<SQLDatabasePage />} />
          <Route path="/settings" element={<ComingSoonPage title="Settings" description="Customise your calculator preferences and default values." />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/methodology" element={<MethodologyPage />} />
          <Route path="/changelog" element={<ChangelogPage />} />

          {/* Pricing guides */}
          <Route path="/guides" element={<GuidesHubPage />} />
          <Route path="/guides/azure-log-analytics-pricing-uk" element={<LogAnalyticsPricingUKPage />} />
          <Route path="/guides/azure-blob-storage-pricing-uk" element={<BlobStoragePricingUKPage />} />
          <Route path="/guides/azure-egress-bandwidth-costs-explained" element={<BandwidthCostsPage />} />
          <Route path="/guides/log-analytics-search-job-vs-restore" element={<SearchJobVsRestorePage />} />
          <Route path="/guides/azure-networking-costs-uk" element={<AzureNetworkingCostsGuide />} />
          <Route path="/guides/azure-data-factory-pricing-uk" element={<AzureDataFactoryPricingGuide />} />
          <Route path="/guides/azure-billing-traps-uk" element={<AzureBillingTrapsPage />} />
          <Route path="/guides/invoice-reconciliation" element={<InvoiceReconciliationPage />} />
          <Route path="/guides/azure-virtual-desktop-pricing-uk" element={<AzureVirtualDesktopCostGuide />} />
          <Route path="/guides/azure-front-door-pricing-uk" element={<AzureFrontDoorPricingGuide />} />
          <Route path="/guides/azure-firewall-pricing-uk" element={<AzureFirewallPricingGuide />} />
          <Route path="/guides/azure-sql-pricing-uk" element={<AzureSQLPricingGuide />} />
          <Route path="/azure-openai" element={<OpenAIPage />} />
          <Route path="/guides/azure-openai-pricing-uk" element={<AzureOpenAIPricingGuide />} />
          <Route path="/managed-disks" element={<ManagedDisksPage />} />
          <Route path="/guides/azure-managed-disks-pricing-uk" element={<AzureManagedDisksPricingGuide />} />
          <Route path="/key-vault" element={<KeyVaultPage />} />
          <Route path="/guides/azure-key-vault-pricing-uk" element={<AzureKeyVaultPricingGuide />} />
          <Route path="/azure-devops" element={<AzureDevOpsPage />} />
          <Route path="/guides/azure-devops-pricing-uk" element={<AzureDevOpsPricingGuide />} />
          <Route path="/site-recovery" element={<SiteRecoveryPage />} />
          <Route path="/guides/azure-site-recovery-pricing-uk" element={<AzureSiteRecoveryPricingGuide />} />
          <Route path="/aks" element={<AksCalculatorPage />} />
          <Route path="/guides/azure-aks-pricing-uk" element={<AzureAksPricingUkGuide />} />
          <Route path="/sql-managed-instance" element={<SqlMiCalculatorPage />} />
          <Route path="/guides/azure-sql-managed-instance-pricing-uk" element={<AzureSqlManagedInstancePricingUkGuide />} />
          <Route path="/application-gateway" element={<ApplicationGatewayPage />} />
          <Route path="/vpn-gateway" element={<VpnGatewayPage />} />
          <Route path="/bastion" element={<BastionPage />} />
          <Route path="/azure-backup" element={<AzureBackupPage />} />
          <Route path="/guides/azure-backup-pricing-uk" element={<AzureBackupPricingUkGuide />} />

          {/* AI/GEO optimized prompt answers */}
          <Route path="/prompt-answers" element={<PromptAnswersPage />} />

          {/* Certification study guides */}
          <Route path="/certifications/sc-200-study-guide-uk" element={<SC200StudyGuidePage />} />
          <Route path="/certifications/az-900-study-guide-uk" element={<AZ900StudyGuidePage />} />
          <Route path="/certifications/az-104-study-guide-uk" element={<AZ104StudyGuidePage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/certifications/az-500-study-guide-uk" element={<AZ500StudyGuidePage />} />
        </Routes>
        </Suspense>
      </Layout>
    </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

export default App;
